import ErrorText from '@data/components/basic/ErrorText/ErrorText';
import LoadingSpinner from '@data/components/basic/LoadingSpinner/LoadingSpinner';
import type {
  FilterContextProps,
  Filters,
  FilterStatus,
} from '@data/context/FilterContext/types';
import { subDays } from 'date-fns';
import { useGetMeApi } from 'eds-common/api/v2';
import { isEmpty } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useViewSetting } from '../ViewSettingsContext';

export const isFilterEmpty = (v: unknown) =>
  Object.hasOwn(v as object, 'length') ? (v as string).length === 0 : false;

export const FiltersContext = createContext<FilterContextProps>({
  filters: {
    products: ['KL'],
    grades: [],
    dateRange: { from: new Date(), to: new Date() },
  },
  mutate: () => {},
  clearFilter: () => {},
  clearAllFilters: () => {},
  enablements: {},
  isDirty: {},
  setDirty: () => {},
});

export interface FiltersProviderProps {
  children: JSX.Element;
}

export function FiltersProvider({ children }: FiltersProviderProps) {
  const {
    data: user,
    isLoading: userLoading,
    isError: userError,
  } = useGetMeApi();

  const currentRole = user?.currentRole;

  const routeParams = useParams();

  const [overviewType] = useViewSetting('overviewType');

  const defaultFilters = useMemo<Filters>(
    () => ({
      products: ['KL'],
      grades: [],
      dateRange: { from: subDays(new Date(), 90), to: new Date() },
    }),
    []
  );

  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [isDirty, setIsDirty] = useState<
    Partial<Record<keyof Filters, boolean>>
  >({});

  // A map to describe under what circumstances a particular filter is enabled,
  // thus visible in the UI
  const enablements = useMemo<FilterStatus>(
    () => ({
      org:
        currentRole === 'administrator' &&
        overviewType === 'org' &&
        !routeParams.teacherId,
      teacher:
        currentRole === 'administrator' &&
        overviewType === 'org' &&
        !routeParams.teacherId,
      products: true,
      class:
        overviewType === 'class' ||
        currentRole === 'teacher' ||
        !!routeParams.teacherId,
      grades: overviewType === 'class' || currentRole === 'teacher',
      student:
        (overviewType === 'class' || currentRole === 'teacher') &&
        !!filters.class &&
        !routeParams.resourceId &&
        !routeParams.familyId,
      dateRange: true,
    }),
    [
      currentRole,
      filters.class,
      overviewType,
      routeParams.teacherId,
      routeParams.resourceId,
      routeParams.familyId,
    ]
  );

  // A map to describe which filters are should reset when another changes.
  // For instance, `{ class: ['student'] }` would mean that when class changes, student would be cleared
  const dependentFilters = useMemo<
    Partial<Record<keyof Filters, (keyof Filters)[]>>
  >(
    () => ({
      class: ['student'],
    }),
    []
  );

  const clearFilter = useCallback(
    (k: keyof Filters) => {
      setIsDirty((ds) => ({ ...ds, [k]: false }));
      setFilters((fs) => ({ ...fs, [k]: defaultFilters[k] }));
    },
    [defaultFilters]
  );

  const mutate = useCallback(
    <K extends keyof Filters>(k: K, filter: Filters[K]) => {
      if (filter == null) {
        throw new Error('null or undefined sent to mutate of FilterContext');
      }

      setIsDirty((ds) => ({ ...ds, [k]: !isFilterEmpty(filter) }));
      setFilters((fs) => ({ ...fs, [k]: filter }));

      // if there are dependent filters, clear them when this filter changes
      const dependentKeys = dependentFilters[k];
      dependentKeys?.forEach((dk) => clearFilter(dk));
    },
    [clearFilter, dependentFilters]
  );

  const clearAllFilters = useCallback(() => {
    setIsDirty({});
    setFilters(defaultFilters);
  }, [defaultFilters]);

  const setDirty = useCallback((k: keyof Filters) => {
    setIsDirty((ds) => ({ ...ds, [k]: true }));
  }, []);

  const value = useMemo<FilterContextProps>(
    () => ({
      filters,
      mutate,
      enablements,
      isDirty,
      setDirty,
      clearFilter,
      clearAllFilters,
      user,
    }),
    [
      clearAllFilters,
      clearFilter,
      enablements,
      filters,
      isDirty,
      mutate,
      setDirty,
      user,
    ]
  );

  if (userLoading)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );

  if (userError) return <ErrorText label="Error fetching user information" />;

  return (
    <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
  );
}

export function useFilter<K extends keyof Filters>(k: K) {
  const {
    filters,
    mutate: mutateBase,
    enablements,
    isDirty,
    setDirty: setDirtyBase,
    clearFilter,
  } = useContext(FiltersContext);

  const mutate = useCallback(
    (filter: Filters[K]) => mutateBase(k, filter),
    [k, mutateBase]
  );

  const setDirty = useCallback(() => setDirtyBase(k), [k, setDirtyBase]);

  const clear = useCallback(() => clearFilter(k), [clearFilter, k]);

  return {
    value: filters[k],
    mutate,
    isEnabled: !!enablements[k],
    isDirty: !!isDirty[k],
    setDirty,
    clear,
  };
}

export function useFilterEnablements() {
  const { enablements } = useContext(FiltersContext);

  return useCallback((k: keyof Filters) => enablements[k], [enablements]);
}

export function useIsDirty() {
  const { isDirty } = useContext(FiltersContext);

  return useCallback((k: keyof Filters) => !!isDirty[k], [isDirty]);
}

export function useSetDirty() {
  const { setDirty } = useContext(FiltersContext);

  return setDirty;
}

export function useAnyDirty() {
  const { isDirty } = useContext(FiltersContext);

  return useMemo(() => !isEmpty(isDirty), [isDirty]);
}

export function useClearAllFilters() {
  const { clearAllFilters } = useContext(FiltersContext);

  return clearAllFilters;
}

export function useAllFilters() {
  const { filters, mutate } = useContext(FiltersContext);

  return [filters, mutate] as const;
}

export function useOrgIdWithFallback() {
  const { filters, user } = useContext(FiltersContext);

  return useMemo(
    () => filters.org ?? user?.currentOrg.orgId ?? '',
    [filters.org, user?.currentOrg.orgId]
  );
}

export function useTeacherIdWithFallback() {
  const { filters, user } = useContext(FiltersContext);

  return useMemo(
    () => filters.teacher ?? user?.userId ?? '',
    [filters.teacher, user?.userId]
  );
}
